/**
 * The Sponsor vs lender comparison, drawn: two tables of eight rows side by
 * side, one figure per side per row, and the gap between them.
 *
 * Every cell has the same two lines — a caption naming where the figure came
 * from (the email line, the model cell, who decided it, "Derived") and the
 * figure — so the grid reads as one uniform block whatever each deal holds,
 * and the full citation is on hover. A cell whose figure is the deal record's
 * own value is drawn by the dossier's EditableSlot, the primitive every other
 * tab edits through, so unlocking the dossier makes it editable here exactly
 * as it is there.
 *
 * The client's column is named by the caller from the client profile
 * (`clientSideLabel`), never a literal.
 *
 * Where the input goes when the dossier is unlocked: always the client's
 * column. Typing a figure on the deal is a person deciding it, which is what
 * that column means, and saving it moves it there for good. The Sponsor column
 * keeps what the sponsor said — the SAVED figure, read-only — so the comparison
 * stays a comparison while it is being edited. An output is never an input box
 * (R1): a field the engine derives on the deal (stored, or the live preview of
 * unsaved edits), and a ratio the client's own figures give. Its cell keeps
 * the client's figure, or its gap, and opens the inputs in place — the same
 * control every other tab draws over a derived value — so Fully Funded's
 * advance and future funding, or LTC's total cost, are reachable from here.
 *
 * What the client's box holds: the client's own figure when the deal holds
 * one. When the deal's figure is the package's, the box starts EMPTY, with the
 * client's reference figure (its internal model's, say) shown as the
 * placeholder. A box holding the package's number put the same figure in both
 * columns beside a variance that said there was nothing to compare, and hid
 * any figure of the client's that is not on the deal. Clearing the box puts
 * the saved value back, so clearing what was never the client's figure
 * changes nothing on Save.
 *
 * An output's inputs open the same way: each is the client's own figure (its
 * model's cell, its decision, its roll-up), shown grey with its source in a
 * box that starts empty — never the deal's value, which may be the sponsor's.
 */
import { useState, type ReactNode } from 'react'

import { ComputedTrigger, EditPopover, activateOnKey, fieldLabel, outputFormulaOf } from './computedInputs'
import { isSystemField, isWritableField, slotKindOf, unwritableReason, type SlotKind } from './editableFields'
import { useDossierEdit, type DossierEdit } from './editContext'
import { EditableSlot, SlotLabel, SlotValueInput, renderCatalogueInput } from './ui'
import {
  DEFAULT_CLIENT_LABEL,
  formatFigure,
  formatVarianceAbsolute,
  formatVarianceRelative,
  isClientOutput,
  slotColumn,
  type ClientInput,
  type ComparisonRow,
  type ComparisonSide,
  type SideFigure,
} from './sponsorArepComparison'

/** Caption and value classes: the same ones EditableSlot's `header` layout draws. */
const CAPTION = 'block truncate font-mono text-[9px] uppercase tracking-widest'
const VALUE = 'block truncate whitespace-nowrap text-[13px]'

function sideName(side: ComparisonSide, client: string): string {
  return side === 'sponsor' ? 'Sponsor' : client
}

/** The client's figure that is NOT the deal's value — its model's, its grid's,
 *  or a ratio worked from them — shown as a reference while the box is empty. */
function referenceFigure(row: ComparisonRow): SideFigure | null {
  return !row.typing && row.dealOwner !== 'arep' && row.arep && !row.arep.onDeal ? row.arep : null
}

/** Unlocked, the client's cell is an output (R1): it opens inputs, never a box. */
function isOutputCell(row: ComparisonRow): boolean {
  return !row.typing && (Boolean(row.dealDerivedFormula) || isClientOutput(row))
}

function hoverText(row: ComparisonRow, side: ComparisonSide, unlocked: boolean, client: string): string {
  const figure = side === 'sponsor' ? row.sponsor : row.arep
  const lines: string[] = []
  if (figure) lines.push(figure.source, ...figure.notes)
  else lines.push(side === 'sponsor' ? row.sponsorGapDetail : row.arepGapDetail)
  if (row.dealDerivedFormula && side === 'arep') {
    lines.push(`The deal record derives this field (${row.dealDerivedFormula}); change its inputs, not the result.`)
  }
  if (unlocked && side === 'arep' && isOutputCell(row) && figure && figure.origin !== 'derived') {
    // The client's figure here is a statement, not the deal's output: it opens nothing.
    lines.push(
      `Editing: the deal's ${row.metric.label} is computed, never typed over. ${client}'s figure here (${figure.tag}) is from outside the deal record and does not recompute from the deal's inputs.`,
    )
  } else if (unlocked && side === 'arep' && isOutputCell(row)) {
    lines.push(`Editing: ${row.metric.label} is computed, never typed over — open it to edit ${client}'s figures it is worked from.`)
  } else if (unlocked && side === 'arep') {
    if (row.dealOwner === 'arep') {
      lines.push(`Editing: the box holds ${client}'s own figure on the deal. A figure typed here and saved replaces it.`)
    } else {
      const whose =
        row.dealOwner === 'sponsor'
          ? " The deal's figure is the package's, and stays under Sponsor."
          : row.dealOwner === 'derived' || row.dealOwner === 'suggested'
            ? " The deal's figure is computed or suggested, not decided."
            : ''
      const reference = referenceFigure(row)
      lines.push(
        `Editing: the box starts empty because the deal holds no figure of ${client}'s own.${whose} A figure typed here and saved is ${client}'s own decision; clearing the box leaves the deal as it is.${
          reference ? ` The grey figure (${reference.tag}) is ${client}'s from outside the deal record, for reference — it is not on the deal.` : ''
        }`,
      )
    }
  }
  return lines.join('\n')
}

function StaticFigure({
  caption,
  value,
  tone,
  captionTone = 'text-stone-600',
}: {
  caption: string
  value: string
  tone: string
  captionTone?: string
}) {
  return (
    <div className="block min-w-0">
      <span className={`${CAPTION} ${captionTone}`}>{caption}</span>
      <span className={`${VALUE} ${tone}`}>{value}</span>
    </div>
  )
}

/** What an empty-start box shows: only an entry that differs from the saved
 *  value — the saved value put back by clearing is not an entry. */
function entryShown(edit: DossierEdit, name: string, saved: unknown): unknown {
  return name in edit.draft && !Object.is(edit.draft[name], saved) ? edit.draft[name] : ''
}

/**
 * A box that starts empty (the draft shows once something is typed), offers
 * a figure of the client's as its placeholder, and puts the saved value back
 * when it is cleared — Save then writes nothing for the field.
 */
function EntryBox({ name, label, kind, saved, placeholder }: {
  name: string
  label: string
  kind: SlotKind
  saved: unknown
  placeholder: string
}) {
  const edit = useDossierEdit()
  return (
    <SlotValueInput
      name={name}
      label={label}
      kind={kind}
      entry={{
        stored: entryShown(edit, name, saved),
        placeholder,
        onChange: (text) => edit.setField(name, text.trim() === '' ? saved : text),
      }}
    />
  )
}

/**
 * The client's box while the deal's figure is not the client's: an EntryBox
 * holding the client's reference figure as its placeholder.
 */
function ClientEntry({ row, client }: { row: ComparisonRow; client: string }) {
  const edit = useDossierEdit()
  const label = `${row.metric.label} (${client})`
  const reference = referenceFigure(row)
  if (!isWritableField(row.field, edit.defs)) {
    // A field Save cannot write reads as it does locked, with the reason on hover.
    return (
      <EditableSlot
        name={row.field}
        label={label}
        caption={reference?.tag ?? row.arepGap}
        display={reference ? formatFigure(reference.value, row.metric.unit) : '—'}
        kind={row.metric.slotKind}
        layout="header"
      />
    )
  }
  const shown = entryShown(edit, row.field, row.saved)
  const caption = row.typing
    ? row.arep ? 'Unsaved' : row.arepGap
    : shown !== '' && shown !== null && shown !== undefined
      ? 'No change'
      : reference?.tag ?? 'Enter figure'
  return (
    <label className="block min-w-0">
      <span className={`${CAPTION} text-stone-600`}>{caption}</span>
      <span className="block whitespace-nowrap text-[13px] text-stone-100">
        <EntryBox
          name={row.field}
          label={label}
          kind={row.metric.slotKind}
          saved={row.saved}
          placeholder={reference ? formatFigure(reference.value, row.metric.unit) : '—'}
        />
      </span>
    </label>
  )
}

/**
 * One input of the client's ratio: its name, the client's figure for it with
 * where that came from, and an EntryBox. A field Save cannot write, one the
 * deal itself derives, and a client figure derived in turn are outputs (R1):
 * shown as the figure, with the reason, never as a box.
 */
function ClientInputRow({ input, client }: { input: ClientInput; client: string }) {
  const edit = useDossierEdit()
  const label = fieldLabel(edit, input.field)
  const figure = input.figure
  const shown = figure ? formatFigure(figure.value, input.unit) : '—'
  const dealFormula = outputFormulaOf(edit, input.field)
  const derived = figure?.origin === 'derived'
  const boxed = isWritableField(input.field, edit.defs) && !isSystemField(input.field) && dealFormula === null && !derived
  const reason = dealFormula !== null
    ? `The deal record derives ${label} (${dealFormula}): correct the figures it is made of.`
    : derived
      ? `${figure.source}. Correct the figures it is made of.`
      : unwritableReason(input.field)
  return (
    <li
      data-edit-input={input.field}
      title={figure ? [figure.source, ...figure.notes].join('\n') : `${client} states no ${label.toLowerCase()}.`}
      className="grid grid-cols-[minmax(0,1fr)_minmax(0,1.2fr)] items-center gap-2 text-[11px]"
    >
      <span className="min-w-0">
        <span className="block truncate text-stone-400">{label}</span>
        <span className="block truncate font-mono text-[8.5px] uppercase tracking-wider text-stone-600">{figure ? figure.tag : 'Not stated'}</span>
      </span>
      {boxed ? (
        <span className="block min-w-0 font-mono text-stone-100">
          <EntryBox
            name={input.field}
            label={`${label} (${client})`}
            kind={slotKindOf(edit.defs.find((def) => def.name === input.field))}
            saved={input.saved}
            placeholder={shown}
          />
        </span>
      ) : (
        <span title={reason} className={`min-w-0 truncate font-mono ${derived || dealFormula !== null ? 'text-violet-300' : 'text-stone-300'}`}>
          {shown}
        </span>
      )}
    </li>
  )
}

/**
 * The client's ratio, or its gap, opened: the figures it is worked from as
 * the client's column reads them. The dossier's ComputedTrigger opens the
 * deal's values instead — on Washington Square its "DSCR (Lender)" popover
 * held the sponsor model's $9,850,000 NOI under a note saying the client's
 * 1.24x recomputes from it, and clearing that box removed the sponsor's NOI
 * on Save. Same trigger and the same popover, so the dossier reads as one.
 */
function ClientRatioInputs({ row, label, client, children }: {
  row: ComparisonRow
  label: string
  client: string
  children: ReactNode
}) {
  const edit = useDossierEdit()
  const [anchor, setAnchor] = useState<HTMLElement | null>(null)
  const formula = row.arepFormula?.trim() || 'computed from other fields'
  const names = row.arepInputs.map((input) => fieldLabel(edit, input.field))
  const open = (target: HTMLElement) => setAnchor((current) => (current ? null : target))
  return (
    <>
      <span
        role="button"
        tabIndex={0}
        data-edit-computed={row.field}
        aria-haspopup="dialog"
        aria-expanded={Boolean(anchor)}
        title={`Computed: ${formula}, from ${client}'s own figures. Edit its inputs — ${names.join(', ')} — not the result.`}
        onClick={(event) => open(event.currentTarget)}
        onKeyDown={activateOnKey(open)}
        className="cursor-pointer underline decoration-violet-300/60 decoration-dotted underline-offset-2 hover:decoration-violet-200 focus-visible:outline-none focus-visible:decoration-solid"
      >
        {children}
      </span>
      {anchor ? (
        <EditPopover
          anchor={anchor}
          title={`${label} · its inputs`}
          note={
            <>
              Computed: <span className="font-mono text-violet-300">{formula}</span>, from {client}&apos;s own figures — each grey
              figure is {client}&apos;s, from where it says. A figure typed here is {client}&apos;s own; clearing a box leaves the
              deal as it is.
            </>
          }
          onClose={() => setAnchor(null)}
        >
          <ul className="space-y-1.5" data-testid="edit-inputs">
            {row.arepInputs.map((input) => (
              <ClientInputRow key={input.field} input={input} client={client} />
            ))}
          </ul>
        </EditPopover>
      ) : null}
    </>
  )
}

/**
 * The client's cell over an output, unlocked: the figure — a ratio worked from
 * the client's own figures — or the gap stays exactly where it was, and opens
 * the inputs it is made of. A box here let a person type 1.25 over a DSCR
 * that its NOI and debt service give, and it never recomputed again (R1).
 * A figure the client states directly on a row the deal derives (its model's
 * Fully Funded at a cell) is no output: it is drawn as it is, opening nothing,
 * since it does not recompute from the deal's inputs.
 */
function OutputCell({ row, figure, gap, client }: { row: ComparisonRow; figure: SideFigure | null; gap: string; client: string }) {
  const edit = useDossierEdit()
  const own = figure?.origin === 'derived'
  const label = `${row.metric.label} (${client})`
  const shown = figure ? formatFigure(figure.value, row.metric.unit) : '—'
  let control: ReactNode = shown
  if ((own || !figure) && row.arepInputs.length > 0) {
    control = <ClientRatioInputs row={row} label={label} client={client}>{shown}</ClientRatioInputs>
  } else if (!figure) {
    // The deal derives it and names no inputs: the formula, on hover.
    control = (
      <ComputedTrigger
        name={row.field}
        label={label}
        formula={outputFormulaOf(edit, row.field) ?? row.dealDerivedFormula}
        inputs={[]}
        renderInput={renderCatalogueInput}
      >
        {shown}
      </ComputedTrigger>
    )
  }
  return (
    <div className="block min-w-0">
      <span className={`${CAPTION} text-stone-600`}>{figure ? figure.tag : gap}</span>
      <span className={`${VALUE} ${figure ? (own ? 'text-violet-300' : 'text-stone-100') : 'text-stone-600'}`}>{control}</span>
    </div>
  )
}

function FigureCell({
  row,
  side,
  unlocked,
  client,
}: {
  row: ComparisonRow
  side: ComparisonSide
  unlocked: boolean
  client: string
}) {
  const figure = side === 'sponsor' ? row.sponsor : row.arep
  const gap = side === 'sponsor' ? row.sponsorGap : row.arepGap
  const slotHere = slotColumn(row, unlocked) === side
  const title = hoverText(row, side, unlocked, client)

  let content
  if (unlocked && side === 'arep' && isOutputCell(row)) {
    content = <OutputCell row={row} figure={figure} gap={gap} client={client} />
  } else if (slotHere && side === 'arep' && unlocked && row.dealOwner !== 'arep') {
    content = <ClientEntry row={row} client={client} />
  } else if (slotHere) {
    // The deal's own value, through the dossier's slot. Its caption says where
    // the figure came from; the field's required marker is on the row's name.
    content = (
      <EditableSlot
        name={row.field}
        label={`${row.metric.label} (${sideName(side, client)})`}
        caption={row.typing && side === 'arep' ? (figure ? 'Unsaved' : gap) : figure?.tag ?? gap}
        display={figure ? formatFigure(figure.value, row.metric.unit) : '—'}
        kind={row.metric.slotKind}
        layout="header"
      />
    )
  } else if (figure) {
    content = (
      <StaticFigure
        caption={figure.tag}
        value={formatFigure(figure.value, row.metric.unit)}
        tone={figure.origin === 'derived' ? 'text-violet-300' : 'text-stone-100'}
      />
    )
  } else {
    content = <StaticFigure caption={gap} value="—" tone="text-stone-600" />
  }

  return (
    <td
      className="min-w-0 px-1 py-0.5 align-middle"
      data-side={side}
      data-origin={figure?.origin ?? 'none'}
      data-slot={slotHere ? '' : undefined}
    >
      <div
        role="group"
        aria-label={`${row.metric.label}, ${sideName(side, client)}`}
        title={title}
        className="min-w-0 overflow-hidden"
      >
        {content}
        {/* The citation itself, where the screen is tall enough to print it
            (1600x950 and up); at 1366x768 it is on hover only. */}
        {figure ? (
          <span className="hidden truncate text-[8.5px] leading-tight text-stone-600 [@media(min-height:880px)]:block">{figure.cite}</span>
        ) : null}
      </div>
    </td>
  )
}

function VarianceCell({ row, client }: { row: ComparisonRow; client: string }) {
  const variance = row.variance
  if (!variance) {
    // Both sides may state a figure and still not compare: a spread is not a rate.
    return (
      <td className="px-1 py-0.5 align-middle" data-variance={row.varianceNote ? 'not-comparable' : 'none'}>
        <div title={row.varianceNote ?? `A variance needs both a Sponsor and a ${client} figure.`} className="min-w-0 overflow-hidden">
          <StaticFigure caption={row.varianceNote ? 'Not comparable' : 'n/a'} value="—" tone="text-stone-600" />
        </div>
      </td>
    )
  }
  const relative = formatVarianceRelative(variance)
  const absolute = formatVarianceAbsolute(variance, row.metric.unit)
  return (
    <td className="px-1 py-0.5 align-middle" data-variance={variance.agrees ? 'agrees' : 'differs'}>
      <div
        title={`${client} − Sponsor: ${absolute}${relative && relative !== absolute ? ` (${relative} of the sponsor's figure)` : ''}`}
        className="min-w-0 overflow-hidden"
      >
        <StaticFigure
          caption={relative}
          captionTone={variance.agrees ? 'text-emerald-300/80' : 'text-amber-300/90'}
          value={absolute}
          tone={variance.agrees ? 'text-stone-500' : 'text-amber-200'}
        />
      </div>
    </td>
  )
}

function ComparisonTable({
  title,
  rows,
  unlocked,
  client,
}: {
  title: string
  rows: ComparisonRow[]
  unlocked: boolean
  client: string
}) {
  return (
    <table aria-label={`Sponsor vs ${client} — ${title}`} className="h-full w-full table-fixed border-collapse text-left leading-tight">
      <colgroup>
        <col className="w-[24%]" />
        <col className="w-[27%]" />
        <col className="w-[27%]" />
        <col className="w-[22%]" />
      </colgroup>
      <thead>
        <tr className="font-mono text-[8px] uppercase tracking-wider text-stone-500">
          <th scope="col" className="truncate px-1 pb-1 font-normal text-amber-200/70">{title}</th>
          <th scope="col" className="px-1 pb-1 font-normal">Sponsor</th>
          <th scope="col" className="truncate px-1 pb-1 font-normal">{client}</th>
          <th scope="col" className="px-1 pb-1 font-normal" title={`${client} − Sponsor, and that gap as a share of the sponsor's figure`}>
            Variance
          </th>
        </tr>
      </thead>
      <tbody>
        {rows.map((row) => (
          // Equal rows: the table fills its half of the panel, and each row
          // takes the same share of it, so the two halves line up row for row.
          <tr key={row.metric.id} data-metric={row.metric.id} className="border-t border-stone-900" style={{ height: `${100 / rows.length}%` }}>
            <th scope="row" className="truncate px-1 py-0.5 align-middle text-[10.5px] font-normal text-stone-300" title={row.metric.label}>
              {/* The catalogue's required marker belongs to the field's name. */}
              <SlotLabel name={row.field} label={row.metric.label} />
            </th>
            <FigureCell row={row} side="sponsor" unlocked={unlocked} client={client} />
            <FigureCell row={row} side="arep" unlocked={unlocked} client={client} />
            <VarianceCell row={row} client={client} />
          </tr>
        ))}
      </tbody>
    </table>
  )
}

/** The two halves, at equal height, and one line saying how to read them. */
export function SponsorArepComparisonView({
  rows,
  unlocked,
  notice,
  clientLabel = DEFAULT_CLIENT_LABEL,
}: {
  rows: ComparisonRow[]
  unlocked: boolean
  /** Said under the tables: what the client's side could not be read from, say. */
  notice?: string | null
  /** The client's column name, from the client profile. */
  clientLabel?: string
}) {
  const request = rows.filter((row) => row.metric.group === 'request')
  const cashflow = rows.filter((row) => row.metric.group === 'cashflow')
  return (
    <div className="flex h-full min-h-0 flex-col gap-1">
      <div className="grid min-h-0 flex-1 grid-cols-1 gap-2 lg:grid-cols-2 lg:gap-0">
        <div className="min-h-0 lg:pr-2">
          <ComparisonTable title="Request & Value" rows={request} unlocked={unlocked} client={clientLabel} />
        </div>
        <div className="min-h-0 lg:border-l lg:border-stone-800/80 lg:pl-2">
          <ComparisonTable title="Cash Flow & Debt" rows={cashflow} unlocked={unlocked} client={clientLabel} />
        </div>
      </div>
      <p className="shrink-0 truncate border-t border-stone-900 pt-1 text-[8.5px] text-stone-600" title={notice ?? undefined}>
        {notice ? `${notice} · ` : ''}
        Sponsor: the package and the sponsor&apos;s model · {clientLabel}: a person&apos;s decision on the deal or the internal model ·
        Derived ratios use that side&apos;s own figures · hover a figure for its source
      </p>
    </div>
  )
}
